import { type Editor } from '@tiptap/core'
import { Decoration } from '@tiptap/pm/view'
import { ReactRenderer } from '@tiptap/react'
import { getAiToolkit } from '@tiptap-pro/ai-toolkit'
import { ReviewActions, type ReviewActionsProps } from './ReviewActions'
import { recordSuggestionDecisions } from './suggestionReview'

type AiToolkit = ReturnType<typeof getAiToolkit>
type ExecuteToolOptions = Parameters<AiToolkit['executeTool']>[0]
type RenderDecorations = NonNullable<
  NonNullable<
    NonNullable<ExecuteToolOptions['reviewOptions']>['displayOptions']
  >['renderDecorations']
>

type ReviewDecorationsConfig = {
  editor: Editor
  documentId: string
  toolkit: AiToolkit
}

export const createReviewDecorations =
  ({
    editor,
    documentId,
    toolkit,
  }: ReviewDecorationsConfig): RenderDecorations =>
  (options) => {
    const { id } = options.suggestion
    const props: ReviewActionsProps = {
      onAccept: () => {
        toolkit.acceptSuggestion(id)
        recordSuggestionDecisions(editor, documentId, 'accepted')
      },
      onReject: () => {
        toolkit.rejectSuggestion(id)
        recordSuggestionDecisions(editor, documentId, 'rejected')
      },
    }

    let renderer: ReactRenderer<unknown, ReviewActionsProps> | undefined
    const widget = Decoration.widget(
      options.range.to,
      () => {
        renderer = new ReactRenderer(ReviewActions, {
          editor,
          props,
          // `as: 'span'` is required as ReactRenderer wraps the component in a
          // `div` by default, and a block element inserted as an inline widget
          // breaks the text onto its own line.
          as: 'span',
        })
        return renderer.element
      },
      {
        side: 1,
        key: `ai-review-actions-${id}`,
        destroy: () => renderer?.destroy(),
      }
    )

    return [...options.defaultRenderDecorations(), widget]
  }
